import React, { useRef, useState } from 'react';
import ReactCrop, {
  Crop,
  PixelCrop,
  centerCrop,
  makeAspectCrop,
} from 'react-image-crop';
import { canvasPreview } from './canvasPreview';
import { useDebounceEffect } from './useDebounceEffect';
import 'react-image-crop/dist/ReactCrop.css';
import { showErrorMessage } from './showMessage';
import { ButtonLarge } from './../app/common/sharedComponents/buttons/ButtonLarge';
import { ButtonBlockColumn } from './../app/common/sharedStyledComponents/GenericStyles.styled';
import styled from 'styled-components';

// This is to demonstate how to make and center a % aspect crop
// which is a bit trickier so we use some helper functions.

const ButtonContainer = styled.div`
  margin-top: 20px;
  width: 100%;
`;

function centerAspectCrop (
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

export default function ImageResize ({
  onImageCompletion,
  uploadImageComponent,
}) {
  const [imgSrc, setImgSrc] = useState('');
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const uploadRef = useRef<any>('');

  const [crop, setCrop] = useState<Crop | undefined>({
    unit: '%',
    x: 25,
    y: 25,
    width: 50,
    height: 50,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [showCropImagePopup, setShowCropImagePopup] = useState(false);

  function onSelectFile (e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      const patternImage = /image-*/;
      const file = e.target.files[0];
      const isImage = file.type.match(patternImage);
      if (!file || !isImage) {
        showErrorMessage(' Invalid file.Please check file size or file type.');
        return;
      }
      setCrop(undefined); // Makes crop preview update between images.
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        if (reader && reader.result) {
          setImgSrc(reader.result.toString() || '');
          setShowCropImagePopup(true);
        }
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  }

  function onImageLoad (e: React.SyntheticEvent<HTMLImageElement>) {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, 16 / 9));
  }

  async function onCropCompletion () {
    const image = imgRef.current;
    const previewCanvas = previewCanvasRef.current;
    if (!image || !previewCanvas || !completedCrop) {
      throw new Error('Crop canvas does not exist');
    }

    // This will size relative to the uploaded image
    // size. If you want to size according to what they
    // are looking at on screen, remove scaleX + scaleY
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const canvas = document.createElement('canvas');
    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('No 2d context');
    }

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      canvas.width,
      canvas.height,
      0,
      0,
      canvas.width,
      canvas.height
    );

    // You might want { type: "image/jpeg", quality: <0 to 1> } to
    // reduce image size
    const fileObj = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob: any) => {
          if (!blob) {
            reject(new Error('Canvas is empty'));
            return;
          }
          const fileName = `${Date.now()}.jpeg`;

          // Create a File from the Blob
          const file = new File([blob], fileName, { type: 'image/jpeg' });

          resolve(file);
        },
        'image/jpeg',
        0.7
      );
    });

    onImageCompletion(fileObj);
  }

  useDebounceEffect(
    async () => {
      if (
        completedCrop &&
        completedCrop.width &&
        completedCrop.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        // We use canvasPreview as it's much faster than imgPreview.
        canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop);
      }
    },
    100,
    [completedCrop]
  );

  return (
    <>
      <div className="Crop-Controls">
        <span onClick={() => uploadRef.current.click()}>
          {uploadImageComponent}
        </span>
        <input
          ref={uploadRef}
          style={{ display: 'none' }}
          type="file"
          accept="image/*"
          onChange={onSelectFile}
        />
      </div>
      {imgSrc && showCropImagePopup && (
        <div className="react-crop" id="react-confirm-alert">
          <div className="react-confirm-alert-overlay overlayPrompt">
            <div className="w-100">
              <div className="backOverlay text-center">
                {!!imgSrc && (
                  <ReactCrop
                    crop={crop}
                    onChange={(_, percentCrop) => setCrop(percentCrop)}
                    onComplete={(c) => setCompletedCrop(c)}
                    ruleOfThirds={true}
                    keepSelection={true}
                  >
                    <img
                      ref={imgRef}
                      alt=""
                      src={imgSrc}
                      onLoad={onImageLoad}
                      className="justify-image-center"
                    />
                  </ReactCrop>
                )}
                {!!completedCrop && (
                  <>
                    <div>
                      <canvas
                        ref={previewCanvasRef}
                        style={{
                          border: '1px solid black',
                          objectFit: 'contain',
                          width: completedCrop.width,
                          height: completedCrop.height,
                          display: 'none',
                        }}
                      />
                    </div>
                  </>
                )}
                <ButtonContainer>
                  <ButtonBlockColumn>
                    <ButtonLarge
                      title="Done"
                      onClick={() => {
                        onCropCompletion();
                        setShowCropImagePopup(false);
                      }}
                    />
                    <ButtonLarge
                      onClick={() => {
                        setShowCropImagePopup(false);
                        setImgSrc('');
                      }}
                      title="Cancel"
                      outlined={true}
                      inverted={true}
                    />
                  </ButtonBlockColumn>
                </ButtonContainer>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
